import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primeicons/primeicons.css'
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { useNavigate } from 'react-router';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import httpService from '../../../services/httpService';
import { ApiEndpoints, Constants } from '../../../constants/appConstants';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useAuthContext } from '../../../Context/AuthContext';
import { ReviewStatusCode, Status } from '../../ProviderProfile/models';
import { Skeleton } from 'primereact/skeleton';
import { Checkbox } from "primereact/checkbox";
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode, FilterOperator, FilterService } from 'primereact/api';
import { useAppContext } from '../../../Context';
import { RHKpiDashboardComponent } from './RHKpiDashboardComponent';
import RHCalcMeasuresModal from './RHCalcMeasuresModal';

const getClientLocalDate = (dateString: any): Date | null => {
    if (!dateString) return null;

    let date: Date;

    if (typeof dateString === 'string') {
        date = new Date(dateString);
    } else if (dateString instanceof Date) {
        date = dateString;
    } else {
        return null;
    }

    return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0,
        0,
        0,
        0
    );
};

FilterService.register('dateIs', (value: any, filter: any) => {
    if (filter === undefined || filter === null) {
        return true;
    }

    if (value === undefined || value === null) {
        return false;
    }

    const normalizedValue = getClientLocalDate(value);
    const normalizedFilter = getClientLocalDate(filter);

    if (!normalizedValue || !normalizedFilter) {
        return false;
    }

    return normalizedValue.getTime() === normalizedFilter.getTime();
});

FilterService.register('dateIsNot', (value: any, filter: any) => {
    if (filter === undefined || filter === null) {
        return true;
    }

    if (value === undefined || value === null) {
        return true;
    }

    const normalizedValue = getClientLocalDate(value);
    const normalizedFilter = getClientLocalDate(filter);

    if (!normalizedValue || !normalizedFilter) {
        return true;
    }

    return normalizedValue.getTime() !== normalizedFilter.getTime();
});

interface RHProvidersComponentProps {
    providerType?: string;
    reviewYear?: number;
}

export const RHProvidersComponent = ({
    providerType: propProviderType,
    reviewYear: propReviewYear
}: RHProvidersComponentProps = {}) => {

    const navigate = useNavigate();

    const [data, setData] = useState<any[]>([]);
    const [kpiData, setKpiData] = useState([]);

    const [IsActiveReview, setIsActiveReview] = useState(Boolean);
    const [IsActiveDispute, setIsActiveDispute] = useState(Boolean);

    const [kpiRefreshTrigger, setKpiRefreshTrigger] = useState(0);
    const [filteredData, setFilteredData] = useState(data);
    const [isLoading, setLoaing] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState<any>(null);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>('');

    const globalSearchKeys = [
        'mpi',
        'providerAgencyName',
        'reviewStatus',
        'currentTier'
    ];

    const [visibleCalcModal, setVisibleCalcModal] = useState(false);

    const {
        appUser,
        isAdministrator,
        isFinalReviewer,
        isInitialReviewer,
        isSmeReviewer
    } = useAuthContext();

    const [filters, setFilters] = useState<any>(null);

    const {
        reviewYear: contextReviewYear,
        providerType: contextProviderType
    } = useAppContext();

    const providerType = propProviderType ?? contextProviderType;
    const reviewYear = propReviewYear ?? contextReviewYear;

    const [statuses, setAllStatuses] = useState<any[]>([]);
    const [markedData, setMarkedData] = useState<any[]>([]);

    const [cellErrors, setCellErrors] = useState<{
        [providerId: string]: string;
    }>({});

    const [showDisputeEndDialog, setShowDisputeEndDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const [reviewCycleLoading, setReviewCycleLoading] = useState(false);
    const [endReviewCycleLoading, setEndReviewCycleLoading] = useState(false);
    const [disputeCycleLoading, setDisputeCycleLoading] = useState(false);
    const [endDisputeCycleLoading, setEndDisputeCycleLoading] = useState(false);

    const logError = (
        operation: string,
        error: any
    ) => {
        console.error(
            `[RHProvidersInfoComponent] ${operation}`,
            {
                message: error?.message,
                responseStatus: error?.response?.status,
                innerExceptionMessage:
                    error?.cause?.message ||
                    error?.response?.data?.innerException?.message,
                stack: error?.stack
            }
        );
    };

    useEffect(() => {
        if (reviewYear) {
            fetchAllProviders();
            getAllStatuses();

            setFilters({
                providerAgencyName: {
                    operator: FilterOperator.AND,
                    constraints: [
                        {
                            value: null,
                            matchMode: FilterMatchMode.STARTS_WITH
                        }
                    ]
                },
                mpi: {
                    operator: FilterOperator.AND,
                    constraints: [
                        {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS
                        }
                    ]
                },
                reviewStatus: {
                    operator: FilterOperator.AND,
                    constraints: [
                        {
                            value: null,
                            matchMode: FilterMatchMode.STARTS_WITH
                        }
                    ]
                },
                reviewerName: {
                    operator: FilterOperator.AND,
                    constraints: [
                        {
                            value: null,
                            matchMode: FilterMatchMode.STARTS_WITH
                        }
                    ]
                },
                appliedTier: {
                    operator: FilterOperator.AND,
                    constraints: [
                        {
                            value: null,
                            matchMode: FilterMatchMode.STARTS_WITH
                        }
                    ]
                },
                dateOfTierAssignment: {
                    operator: FilterOperator.AND,
                    constraints: [
                        {
                            value: null,
                            matchMode: FilterMatchMode.DATE_IS
                        }
                    ]
                },
                currentTier: {
                    operator: FilterOperator.AND,
                    constraints: [
                        {
                            value: null,
                            matchMode: FilterMatchMode.STARTS_WITH
                        }
                    ]
                },
                tierChangeStatus: {
                    operator: FilterOperator.AND,
                    constraints: [
                        {
                            value: null,
                            matchMode: FilterMatchMode.STARTS_WITH
                        }
                    ]
                },
                smeInvolvement: {
                    operator: FilterOperator.AND,
                    constraints: [
                        {
                            value: null,
                            matchMode: FilterMatchMode.STARTS_WITH
                        }
                    ]
                },
                passingMeasures: {
                    operator: FilterOperator.AND,
                    constraints: [
                        {
                            value: null,
                            matchMode: FilterMatchMode.STARTS_WITH
                        }
                    ]
                }
            });
        }
    }, [reviewYear]);

    useEffect(() => {
        httpService
            .get(`${ApiEndpoints.IsActiveReview}/${providerType}`)
            .then((response: any) => {
                setIsActiveReview(response?.data);
            })
            .catch((error: any) => {
                logError(
                    'Error fetching active review cycle.',
                    error
                );
            });

        IsActiveDisputeApi();
    }, [providerType]);

    useEffect(() => {
        if (markedData && markedData.length > 0) {
            const updatedData = data.map((provider: any) => {
                const markedProvider = markedData.find(
                    (md) => md.providerId === provider.providerId
                );

                return markedProvider
                    ? { ...provider, ...markedProvider }
                    : provider;
            });

            setData(updatedData);
            setFilteredData(updatedData);
        }
    }, [markedData]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const filterButtons = document.querySelectorAll(
                '.p-column-filter-menu-button'
            );

            filterButtons.forEach((btn) => {
                btn.setAttribute('title', 'Filter');
            });
        }, 300);

        return () => clearTimeout(timer);
    }, [isLoading, filteredData]);

    const IsActiveDisputeApi = () => {
        httpService
            .get(`${ApiEndpoints.IsActiveDisputeCycle}/${providerType}`)
            .then((response: any) => {
                setIsActiveDispute(response?.data);
            })
            .catch((error: any) => {
                logError(
                    'Error fetching active resolution cycle.',
                    error
                );
            });
    };

    const getDateOnly = (date: string) => {
        return getClientLocalDate(date);
    };

    const fetchAllProviders = () => {
        if (!reviewYear) {
            console.warn(
                '[RHProvidersInfoComponent] Review year is not available.'
            );

            setData([]);
            setFilteredData([]);
            setKpiData([]);
            setLoaing(false);

            return;
        }

        setLoaing(true);

        httpService
            .get(`${ApiEndpoints.getRsProvidersData}/${reviewYear}`)
            .then((response: any) => {
                if (
                    !Array.isArray(response?.data) ||
                    response.data.length === 0
                ) {
                    console.warn(
                        '[RHProvidersInfoComponent] No providers found.'
                    );
                }

                const updatedData = (response?.data || []).map(
                    (item: any) => {
                        const currentTier = item.currentTier;
                        const desiredTier = item.appliedTier;

                        let computedTierChangeStatus = '';

                        if (
                            currentTier &&
                            currentTier.toLowerCase() !== 'unassigned'
                        ) {
                            computedTierChangeStatus =
                                desiredTier &&
                                desiredTier === currentTier
                                    ? 'No'
                                    : 'Yes';
                        }

                        return {
                            ...item,
                            dateOfTierAssignment:
                                getDateOnly(item.dateOfTierAssignment),
                            tierChangeStatus:
                                computedTierChangeStatus
                        };
                    }
                );

                setData(updatedData);
                setFilteredData(updatedData);
                setKpiData(response?.data || []);
            })
            .catch((error: any) => {
                logError(
                    'Error fetching residential providers.',
                    error
                );

                setData([]);
                setFilteredData([]);
                setKpiData([]);
            })
            .finally(() => {
                setLoaing(false);
            });
    };

    const parentSearchHandler = useCallback(
        (
            value: any,
            searchColumns: string[],
            exactMatch: boolean = false
        ) => {
            let filtered = filteredData.filter((item: any) =>
                searchColumns.some((key) => {
                    const fieldValue = String(item[key]).toLowerCase();

                    if (exactMatch) {
                        return fieldValue === value?.toLowerCase();
                    }

                    return fieldValue.includes(value?.toLowerCase());
                })
            );

            if (value == globalFilterValue) {
                value = "";
                filtered = data;
            }

            if (searchColumns.includes("currentTier")) {
                filtered = filtered.filter((item: any) => {
                    return (
                        item["reviewStatus"] === "Review Completed" ||
                        item["reviewStatus"] ===
                            "Resolution Review Completed"
                    );
                });
            }

            setGlobalFilterValue(value);
            setFilteredData(filtered);
        },
        [data]
    );

    const handleSearch = (e: any) => {
        let value = "";

        if (
            Array.isArray(e) &&
            typeof e.join('') == "string"
        ) {
            value = e.join('');
        } else {
            value = e.target.value;
        }

        let filtered = data.filter((item: any) =>
            globalSearchKeys.some((key) => {
                const fieldValue = String(item[key]).toLowerCase();

                return fieldValue.includes(
                    value?.toLowerCase()
                );
            })
        );

        if (value == globalFilterValue) {
            value = '';
            filtered = data;
        }

        setGlobalFilterValue(value);
        setFilteredData(filtered);
    };

    const BeginReviewCycle = () => {
        setReviewCycleLoading(true);

        httpService
            .get(`${ApiEndpoints.BeginReviewCycle}/${providerType}`)
            .then((response: any) => {
                if (response.status === 200) {
                    setIsActiveReview(true);
                    fetchAllProviders();
                    setKpiRefreshTrigger(
                        (prev) => prev + 1
                    );
                }
            })
            .catch((error: any) => {
                logError(
                    'Error beginning review cycle.',
                    error
                );
            })
            .finally(() => {
                setReviewCycleLoading(false);
            });
    };

    const EndReviewCycle = () => {
        setEndReviewCycleLoading(true);

        httpService
            .get(`${ApiEndpoints.EndReviewCycle}/${providerType}`)
            .then((response: any) => {
                if (response.status === 200) {
                    setIsActiveReview(false);
                }
            })
            .catch((error: any) => {
                logError(
                    'Error ending review cycle.',
                    error
                );
            })
            .finally(() => {
                setEndReviewCycleLoading(false);
            });
    };

    const BeginDisputeCycle = () => {
        setDisputeCycleLoading(true);

        httpService
            .get(`${ApiEndpoints.BeginDisputeCycle}/${providerType}`)
            .then((response: any) => {
                if (response.status === 200) {
                    setIsActiveDispute(true);
                    ChangeReviewStatusForDispute();
                }
            })
            .catch((error: any) => {
                logError(
                    'Error beginning resolution cycle.',
                    error
                );
            })
            .finally(() => {
                setDisputeCycleLoading(false);
            });
    };

    const EndDisputeCycle = () => {
        setEndDisputeCycleLoading(true);

        httpService
            .get(`${ApiEndpoints.EndDisputeCycle}/${providerType}`)
            .then((response: any) => {
                if (response.status === 200) {
                    setIsActiveDispute(false);
                }
            })
            .catch((error: any) => {
                logError(
                    'Error ending resolution cycle.',
                    error
                );
            })
            .finally(() => {
                setEndDisputeCycleLoading(false);
                fetchAllProviders();

                setKpiRefreshTrigger(
                    (prev) => prev + 1
                );
            });
    };

    const ChangeReviewStatusForDispute = () => {
        httpService
            .get(
                `${ApiEndpoints.ChangeReviewStatusForDispute}/${providerType}`
            )
            .catch((error: any) => {
                logError(
                    'Error changing review status for resolution cycle.',
                    error
                );
            })
            .finally(() => {
                fetchAllProviders();

                setKpiRefreshTrigger(
                    (prev) => prev + 1
                );
            });
    };

    const confirmEndDisputeCycle = () => {
        setShowDisputeEndDialog(true);
    };

    const onConfirmEndDispute = () => {
        setShowDisputeEndDialog(false);
        EndDisputeCycle();
    };

    const onCancelEndDispute = () => {
        setShowDisputeEndDialog(false);
    };

    const confirmEndReview = () => {
        setShowConfirmDialog(true);
    };

    const onConfirmEndReview = () => {
        setShowConfirmDialog(false);
        EndReviewCycle();
    };

    const onCancelEndReview = () => {
        setShowConfirmDialog(false);
    };

    const onSelectedProviderChange = (e: any) => {
        navigate(
            `/provider`,
            {
                state: {
                    provider: e.value,
                    providerType,
                    reviewYear
                }
            }
        );

        setSelectedProvider(e.value);
    };

    const markAsCompletedBodyTemplate = (rowData: any) => {
        return (
            <div
                className="flex align-items-center justify-content-center"
                onClick={(e) => e.stopPropagation()}
                style={{ flexDirection: "column" }}
            >
                {!rowData.isCalculating && (
                    <span
                        className={
                            rowData.reviewStatusCode?.toLowerCase() !==
                                ReviewStatusCode.ReviewCompleted?.toLowerCase() &&
                            (isFinalReviewer() || isAdministrator()) &&
                            !(
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsReviewCompleted?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsSMEReview?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsNotStarted?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsInitialReview?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.NotStarted?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsResolutionInitialReview?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsResolutionNotStarted?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsResolutionSMEReview?.toLowerCase() ||
                                !(isFinalReviewer() || isAdministrator()) ||
                                (
                                    IsActiveDispute &&
                                    rowData.reviewStatusCode?.toLowerCase() ===
                                        ReviewStatusCode.RsResolutionReviewCompleted?.toLowerCase()
                                )
                            )
                                ? "checkbox-hoverable"
                                : ""
                        }
                        style={{ display: "inline-block" }}
                    >
                        <Checkbox
                            checked={
                                rowData.reviewStatus ===
                                    "Review Completed" ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsResolutionReviewCompleted?.toLowerCase()
                            }
                            disabled={
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsReviewCompleted?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsSMEReview?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsNotStarted?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.RsInitialReview?.toLowerCase() ||
                                rowData.reviewStatusCode?.toLowerCase() ===
                                    ReviewStatusCode.NotStarted?.toLowerCase() ||
                                !(isFinalReviewer() || isAdministrator()) ||
                                (
                                    IsActiveDispute &&
                                    rowData.reviewStatusCode?.toLowerCase() ===
                                        ReviewStatusCode.RsResolutionReviewCompleted?.toLowerCase()
                                )
                            }
                            onChange={(e: any) => {
                                handleReviewComplete(
                                    rowData,
                                    e.checked
                                );
                            }}
                            className="p-checkbox-success"
                        />
                    </span>
                )}

                {rowData.isCalculating && (
                    <i
                        className="pi pi-spin pi-spinner"
                        style={{
                            fontSize: "1.2em",
                            marginTop: "4px",
                            color: "#26890D"
                        }}
                    />
                )}

                {cellErrors[rowData.providerId] && (
                    <span
                        style={{
                            color: "red",
                            fontSize: "12px",
                            marginTop: "4px"
                        }}
                    >
                        {cellErrors[rowData.providerId]}
                    </span>
                )}

                <style>
                    {`
                        .checkbox-hoverable .p-checkbox-box {
                            transition: box-shadow 0.2s, border-color 0.2s;
                        }

                        .checkbox-hoverable:hover .p-checkbox-box {
                            box-shadow: 0 0 0 2px #26890D33;
                            border-color: #26890D;
                            cursor: pointer;
                        }
                    `}
                </style>
            </div>
        );
    };

    const getAllStatuses = () => {
        httpService
            .get<Status[]>(
                `${ApiEndpoints.GetStatus}?providerType=${providerType}`
            )
            .then((response) => {
                setAllStatuses(response.data);
            })
            .catch((error: any) => {
                logError(
                    'Error fetching statuses.',
                    error
                );
            });
    };

    const handleReviewComplete = (
        rowData: any,
        isSelected: true
    ) => {
        setCellErrors((prev) => ({
            ...prev,
            [rowData.providerId]: ""
        }));

        if (
            !isFinalReviewer() &&
            !isAdministrator()
        ) {
            setCellErrors((prev) => ({
                ...prev,
                [rowData.providerId]:
                    "Only Final Reviewer can mark review as completed."
            }));

            return;
        }

        if (rowData.isCalculating) {
            return;
        }

        if (isSelected) {
            const commonCompletedStatus =
                IsActiveDispute
                    ? ReviewStatusCode.RsResolutionReviewCompleted
                    : ReviewStatusCode.RsReviewCompleted;

            const completedStatus = statuses.find(
                (s) =>
                    s.statusCode.toLowerCase() ===
                    commonCompletedStatus.toLowerCase()
            );

            const updatedData = data.map(
                (provider: any) =>
                    provider.providerId === rowData.providerId
                        ? {
                            ...provider,
                            isCalculating: true
                        }
                        : provider
            );

            setData(updatedData);
            setFilteredData(updatedData);

            setMarkedData((prev) =>
                prev.filter(
                    (md) =>
                        md.providerId !==
                        rowData.providerId
                )
            );

            httpService
                .post(
                    ApiEndpoints.MarkAsCompleted,
                    {
                        ProviderId:
                            rowData.providerId,
                        StatusId:
                            completedStatus?.statusId,
                        reviewerName:
                            appUser?.name ??
                            appUser?.email,
                        ReviewYear:
                            reviewYear,
                        MpiId:
                            rowData.mpi,
                        providerType:
                            providerType
                    }
                )
                .then((response: any) => {
                    if (response.status === 200) {
                        const responseData =
                            response?.data;

                        responseData.isCalculating =
                            false;

                        setMarkedData([
                            ...markedData,
                            responseData
                        ]);

                        setCellErrors((prev) => ({
                            ...prev,
                            [rowData.providerId]: ""
                        }));

                        syncReviewStatusInIntakePortal(
                            completedStatus?.statusId!,
                            rowData.providerId,
                            rowData.mpi
                        );
                    }
                })
                .catch((error: any) => {
                    logError(
                        'Error marking review as completed.',
                        error
                    );

                    const resetData = data.map(
                        (provider: any) =>
                            provider.providerId ===
                            rowData.providerId
                                ? {
                                    ...provider,
                                    isCalculating:
                                        false
                                }
                                : provider
                    );

                    setData(resetData);
                    setFilteredData(resetData);

                    setCellErrors((prev) => ({
                        ...prev,
                        [rowData.providerId]:
                            error?.response?.data?.message ||
                            error?.message ||
                            "Error updating review status."
                    }));
                });
        }
    };

    const syncReviewStatusInIntakePortal = (
        statusId: number,
        providerId: number,
        mpi: string
    ) => {
        httpService
            .post(
                ApiEndpoints.SyncProviderStatusWithIntake,
                {
                    ProviderId: providerId,
                    StatusId: statusId,
                    reviewerEmail:
                        appUser?.email ??
                        appUser?.name,
                    MpiId: mpi
                }
            )
            .then((response: any) => {
                if (response.status === 200) {
                    console.log(
                        '[RHProvidersInfoComponent] Review status synchronized with Intake.'
                    );
                }
            })
            .catch((error: any) => {
                logError(
                    'Error synchronizing review status with Intake.',
                    error
                );
            });
    };

    const renderHeader = () => {
        return (
            <div className="flex flex-wrap gap-2 justify-content-between ">
                <IconField
                    iconPosition="left"
                    style={{ alignItems: 'end' }}
                >
                    <InputIcon className="pi pi-search" />

                    <InputText
                        value={globalFilterValue}
                        onChange={handleSearch}
                        placeholder="Search Provider"
                    />
                </IconField>
            </div>
        );
    };

    const showDateOfTierAssignment = (
        rowData: any
    ) => {
        if (rowData.dateOfTierAssignment) {
            const localDate =
                getClientLocalDate(
                    rowData.dateOfTierAssignment
                );

            return localDate
                ? localDate.toLocaleDateString()
                : '';
        }
    };

    const getKpiIsEnabled = () =>
        Constants.KPI_IS_ENABLED;

    const tierChangeStatusTemplate = (
        rowData: any
    ) => {
        const desiredTier =
            rowData.appliedTier;

        const currentTier =
            rowData.currentTier;

        if (
            !currentTier ||
            currentTier.toLowerCase() ===
                'unassigned'
        ) {
            return '';
        }

        return (
            desiredTier &&
            currentTier &&
            desiredTier === currentTier
                ? 'No'
                : 'Yes'
        );
    };

    const dateFilterTemplate = (
        options: any
    ) => {
        return (
            <Calendar
                value={options.value}
                onChange={(e) => {
                    const selectedDate =
                        e.value;

                    if (selectedDate) {
                        const clientDate =
                            new Date(
                                selectedDate.getFullYear(),
                                selectedDate.getMonth(),
                                selectedDate.getDate(),
                                0,
                                0,
                                0,
                                0
                            );

                        options.filterCallback(
                            clientDate,
                            options.index
                        );
                    } else {
                        options.filterCallback(
                            null,
                            options.index
                        );
                    }
                }}
                dateFormat="mm/dd/yy"
                placeholder="mm/dd/yyyy"
                mask="99/99/9999"
            />
        );
    };

    const skeletonBodyTemplate = () => (
        <Skeleton
            width="75%"
            height="1.2rem"
            style={{
                backgroundColor: '#e9ecef',
                borderRadius: '4px'
            }}
        />
    );

    const skeletonRows = useMemo(
        () =>
            Array.from(
                { length: 5 },
                (_, i) => ({
                    providerId:
                        `skeleton-${i}`,
                    providerAgencyName:
                        '',
                    mpi:
                        '',
                    reviewStatus:
                        '',
                    reviewerName:
                        '',
                    appliedTier:
                        '',
                    dateOfTierAssignment:
                        null,
                    currentTier:
                        '',
                    tierChangeStatus:
                        ''
                })
            ),
        []
    );

    interface ColumnConfig {
        field: string;
        header: string;
        filterPlaceholder: string;
        dataType?: string;
        filterElement?: (
            options: any
        ) => JSX.Element;
        bodyTemplate?: (
            rowData: any
        ) => any;
    }

    const isReviewerRole =
        isInitialReviewer() ||
        isSmeReviewer();

    const columns: ColumnConfig[] = [
        {
            field: 'providerAgencyName',
            header: 'Agency Name',
            filterPlaceholder:
                'Filter by Agency Name'
        },
        {
            field: 'mpi',
            header: 'MPI',
            filterPlaceholder:
                'Filter by MPI'
        },
        {
            field: 'reviewStatus',
            header: 'Status',
            filterPlaceholder:
                'Filter by Agency Status'
        },
        {
            field: 'reviewerName',
            header: 'Reviewer',
            filterPlaceholder:
                'Filter by Reviewer'
        },
        ...(isReviewerRole
            ? [
                {
                    field: 'appliedTier',
                    header: 'Desired Tier',
                    filterPlaceholder:
                        'Filter by Desired Tier'
                }
            ]
            : [
                {
                    field: 'currentTier',
                    header:
                        'Tier Determination',
                    filterPlaceholder:
                        'Filter by Tier'
                },
                {
                    field:
                        'dateOfTierAssignment',
                    header:
                        'Determination Date',
                    filterPlaceholder:
                        'Filter by Determination Date',
                    dataType:
                        'date',
                    filterElement:
                        dateFilterTemplate,
                    bodyTemplate:
                        showDateOfTierAssignment
                },
                {
                    field:
                        'tierChangeStatus',
                    header:
                        'Tier Change Status',
                    filterPlaceholder:
                        'Filter by Tier Change Status',
                    bodyTemplate:
                        tierChangeStatusTemplate
                },
                {
                    field:
                        'smeInvolvement',
                    header:
                        'SME Involvement',
                    filterPlaceholder:
                        'Filter by SME Involvement',
                    bodyTemplate:
                        (rowData: any) =>
                            rowData.smeInvolvement +
                            ' %'
                },
                {
                    field:
                        'passingMeasures',
                    header:
                        'Passing Measures',
                    filterPlaceholder:
                        'Filter by Passing Measures',
                    bodyTemplate:
                        (rowData: any) =>
                            rowData.passingMeasures +
                            ' %'
                }
            ])
    ];

    const getColumnBody = (
        col: ColumnConfig
    ) => {
        if (isLoading) {
            return skeletonBodyTemplate;
        }

        if (col.bodyTemplate) {
            return col.bodyTemplate;
        }

        return undefined;
    };

    const getColumnHeaderStyle = (
        minWidth: string,
        width: string = minWidth
    ): React.CSSProperties => ({
        textAlign: "left",
        minWidth,
        background: "#f8f9fa",
        width
    });

    const getColumnBodyStyle = (
        width: string = "120px",
        textAlign: string = "center"
    ): React.CSSProperties => ({
        textAlign:
            textAlign as
                | "left"
                | "center"
                | "right",
        background: "#fff",
        width,
        wordBreak: "break-word",
        whiteSpace: "pre-line"
    });

    const header = renderHeader();

    return (
        <>
            <div className="card">
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        gap: '10px',
                        padding: '15px 25px',
                        height: '80px',
                        minHeight: '5rem'
                    }}
                >
                    {isAdministrator() &&
                        !isLoading && (
                            <>
                                {IsActiveReview ==
                                    false &&
                                    IsActiveDispute ==
                                    false && (
                                        <Button
                                            size='small'
                                            className="p-button-secondary"
                                            onClick={
                                                BeginReviewCycle
                                            }
                                            disabled={
                                                reviewCycleLoading
                                            }
                                        >
                                            {reviewCycleLoading ? (
                                                <>
                                                    <i
                                                        className="pi pi-spin pi-spinner"
                                                        style={{
                                                            marginRight:
                                                                8
                                                        }}
                                                    />
                                                    Begin Review Cycle
                                                </>
                                            ) : (
                                                "Begin Review Cycle"
                                            )}
                                        </Button>
                                    )}

                                {IsActiveReview ==
                                    true &&
                                    IsActiveDispute ==
                                    false && (
                                        <Button
                                            size='small'
                                            className="p-button-secondary p-button-danger"
                                            onClick={
                                                confirmEndReview
                                            }
                                            disabled={
                                                endReviewCycleLoading ||
                                                disputeCycleLoading
                                            }
                                        >
                                            {endReviewCycleLoading ? (
                                                <>
                                                    <i
                                                        className="pi pi-spin pi-spinner"
                                                        style={{
                                                            marginRight:
                                                                8
                                                        }}
                                                    />
                                                    End Review Cycle
                                                </>
                                            ) : (
                                                "End Review Cycle"
                                            )}
                                        </Button>
                                    )}

                                {IsActiveReview ==
                                    true &&
                                    IsActiveDispute ==
                                    false && (
                                        <Button
                                            size='small'
                                            className="p-button-secondary"
                                            onClick={
                                                BeginDisputeCycle
                                            }
                                            disabled={
                                                disputeCycleLoading ||
                                                endReviewCycleLoading
                                            }
                                        >
                                            {disputeCycleLoading ? (
                                                <>
                                                    <i
                                                        className="pi pi-spin pi-spinner"
                                                        style={{
                                                            marginRight:
                                                                8
                                                        }}
                                                    />
                                                    Begin Resolution Cycle
                                                </>
                                            ) : (
                                                "Begin Resolution Cycle"
                                            )}
                                        </Button>
                                    )}

                                {IsActiveReview ==
                                    true &&
                                    IsActiveDispute ==
                                    true && (
                                        <Button
                                            size='small'
                                            className="p-button-secondary p-button-danger"
                                            onClick={
                                                confirmEndDisputeCycle
                                            }
                                            disabled={
                                                endDisputeCycleLoading
                                            }
                                        >
                                            {endDisputeCycleLoading ? (
                                                <>
                                                    <i
                                                        className="pi pi-spin pi-spinner"
                                                        style={{
                                                            marginRight:
                                                                8
                                                        }}
                                                    />
                                                    End Resolution Cycle
                                                </>
                                            ) : (
                                                "End Resolution Cycle"
                                            )}
                                        </Button>
                                    )}

                                <Button
                                    size='small'
                                    className="p-button-secondary"
                                    onClick={() =>
                                        setVisibleCalcModal(
                                            true
                                        )
                                    }
                                >
                                    Perform Calculations
                                </Button>
                            </>
                        )}
                </div>

                <div className="kpi-pie-chart-wrapper">
                    {getKpiIsEnabled() && (
                        <RHKpiDashboardComponent
                            key={
                                kpiRefreshTrigger
                            }
                            providersData={
                                kpiData
                            }
                            searchHandler={
                                parentSearchHandler
                            }
                            reviewYear={
                                reviewYear
                            }
                        />
                    )}
                </div>

                <RHCalcMeasuresModal
                    isOpen={
                        visibleCalcModal
                    }
                    onClose={() => {
                        setVisibleCalcModal(
                            false
                        );
                    }}
                    reviewYear={
                        reviewYear
                    }
                />

                <Dialog
                    header="Confirmation"
                    visible={
                        showConfirmDialog
                    }
                    onHide={
                        onCancelEndReview
                    }
                    footer={
                        <div
                            style={{
                                display:
                                    'flex',
                                justifyContent:
                                    'space-between'
                            }}
                        >
                            <Button
                                label="No"
                                icon="pi pi-times"
                                onClick={
                                    onCancelEndReview
                                }
                                className="p-button-text"
                            />

                            <Button
                                label="Yes"
                                icon="pi pi-check"
                                onClick={
                                    onConfirmEndReview
                                }
                                autoFocus
                            />
                        </div>
                    }
                >
                    <div>
                        {data.some(
                            (
                                provider: any
                            ) =>
                                provider.reviewStatusCode?.toLowerCase() !==
                                ReviewStatusCode.ReviewCompleted?.toLowerCase()
                        ) && (
                            <p
                                style={{
                                    color:
                                        'red'
                                }}
                            >
                                Some providers' reviews are not yet completed.
                            </p>
                        )}

                        <p>
                            Are you sure you want to{' '}
                            <b>
                                End the review cycle
                            </b>
                            ?
                        </p>
                    </div>
                </Dialog>

                <Dialog
                    header="Confirmation"
                    visible={
                        showDisputeEndDialog
                    }
                    onHide={
                        onCancelEndDispute
                    }
                    footer={
                        <div
                            style={{
                                display:
                                    'flex',
                                justifyContent:
                                    'space-between'
                            }}
                        >
                            <Button
                                label="No"
                                icon="pi pi-times"
                                onClick={
                                    onCancelEndDispute
                                }
                                className="p-button-text"
                            />

                            <Button
                                label="Yes"
                                icon="pi pi-check"
                                onClick={
                                    onConfirmEndDispute
                                }
                                autoFocus
                            />
                        </div>
                    }
                >
                    <p>
                        Are you sure you want to{' '}
                        <b>
                            End the Resolution Cycle
                        </b>
                        ?
                    </p>
                </Dialog>

                <div>
                    <DataTable
                        value={
                            isLoading
                                ? skeletonRows
                                : filteredData
                        }
                        paginator
                        header={
                            header
                        }
                        rows={5}
                        rowsPerPageOptions={[
                            5,
                            10,
                            25,
                            50
                        ]}
                        filters={
                            filters
                        }
                        selectionMode="single"
                        selection={
                            selectedProvider
                        }
                        onSelectionChange={
                            onSelectedProviderChange
                        }
                        dataKey="providerId"
                        emptyMessage="No provider found."
                    >
                        {(isAdministrator() ||
                            isFinalReviewer()) && (
                            <Column
                                header="Mark as Completed"
                                body={
                                    markAsCompletedBodyTemplate
                                }
                                headerStyle={getColumnHeaderStyle(
                                    "100px"
                                )}
                                bodyStyle={getColumnBodyStyle(
                                    "100px"
                                )}
                            />
                        )}

                        {columns.map(
                            (col) => (
                                <Column
                                    key={
                                        col.field
                                    }
                                    field={
                                        col.field
                                    }
                                    header={
                                        col.header
                                    }
                                    sortable={
                                        !isLoading
                                    }
                                    filter={
                                        !isLoading
                                    }
                                    filterPlaceholder={
                                        col.filterPlaceholder
                                    }
                                    style={{
                                        minWidth:
                                            '14rem'
                                    }}
                                    headerStyle={{
                                        whiteSpace:
                                            'nowrap'
                                    }}
                                    body={getColumnBody(
                                        col
                                    )}
                                    dataType={
                                        col.dataType as any
                                    }
                                    filterElement={
                                        col.filterElement
                                    }
                                />
                            )
                        )}
                    </DataTable>
                </div>
            </div>
        </>
    );
}